/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    createContext,
    useContext,
    useState,
    useCallback,
    useRef,
    useSyncExternalStore,
    type ComponentPropsWithoutRef,
    type ReactElement,
} from 'react';
import { NavLink } from '@/components/link';
import { useNavigate } from '@/hooks/use-navigate';
import type { ShopperProducts } from '@/scapi';
import CategoryNavigationMenu, { WithCategoryNavigationMenu } from '@/components/navigation-menu';
import { Button } from '@/components/ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toImageUrl, transformHtmlImageUrls } from '@/lib/images/dynamic-image';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { NavigationMenuLink } from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import { useSubCategory } from '@/components/navigation-menu/context';
import { routes, routeHref } from '@/route-paths';
import { createNavContentSlotStore } from '@/components/navigation-menu-mega/nav-content-slot-store';
import { useNavContentSlotLoader } from '@/components/navigation-menu-mega/use-nav-content-slot-loader';
import CategoryDropdownContentSlot from '@/components/navigation-menu-mega/category-dropdown-content-slot';
import CategoryDropdownRibbon from '@/components/navigation-menu-mega/category-dropdown-ribbon';
import type { PageWithComponentData } from '@/lib/page-designer/page-loader.server';

export type { NavContentSlotEntry } from '@/components/navigation-menu-mega/nav-content-slot-store';

interface MobileMenuContextType {
    isOpen: boolean;
    toggle: () => void;
    close: () => void;
    categories: ShopperProducts.schemas['Category'][];
}

const MobileMenuContext = createContext<MobileMenuContextType | null>(null);

const DONATE_HREF = 'https://giftshop.stjude.org/gift-of-donation-pd.html';

// eslint-disable-next-line react-refresh/only-export-components
export function useMobileMenu() {
    return useContext(MobileMenuContext);
}

function hasBanner(category?: ShopperProducts.schemas['Category']): category is ShopperProducts.schemas['Category'] {
    return typeof category?.c_headerMenuBanner === 'string' && category?.c_headerMenuBanner?.length > 0;
}

function isVertical(category?: ShopperProducts.schemas['Category']): category is ShopperProducts.schemas['Category'] {
    // Default to vertical if not set
    if (!category?.c_headerMenuOrientation) {
        return true;
    }
    // Only horizontal if explicitly set to "horizontal"
    return String(category.c_headerMenuOrientation).toLowerCase() !== 'horizontal';
}

/**
 * True when the given page exists and its named region has at least one authored
 * component — guards against reserving a grid column, or rendering a wrapper, for a
 * Page Designer page that exists but is still empty.
 */
function hasRegionContent(
    page: PageWithComponentData | null | undefined,
    regionId: string
): page is PageWithComponentData {
    return Boolean(page?.regions?.find((region) => region.id === regionId)?.components?.length);
}

function CategoryBanner({
    category,
    ...props
}: ComponentPropsWithoutRef<'a'> & { category: ShopperProducts.schemas['Category'] }) {
    const config = useConfig();
    const imageSrc = toImageUrl({ src: (category?.c_slotBannerImage as string) ?? '', config });

    // Transform any image URLs in the HTML banner to use DIS with WebP optimization
    const transformedBannerHtml = transformHtmlImageUrls((category.c_headerMenuBanner as string) || '', config);

    return (
        <NavigationMenuLink asChild>
            <NavLink {...props} to={routeHref(routes.category, { categoryId: category.id })}>
                {imageSrc ? (
                    <img
                        className="object-contain w-full max-w-full max-h-[512px]"
                        src={imageSrc}
                        alt={category.name}
                    />
                ) : (
                    // eslint-disable-next-line react/no-danger
                    <div className="ml-auto" dangerouslySetInnerHTML={{ __html: transformedBannerHtml }} />
                )}
            </NavLink>
        </NavigationMenuLink>
    );
}

function hasSubcategories(category: ShopperProducts.schemas['Category']): boolean {
    return (
        typeof category.onlineSubCategoriesCount === 'number' &&
        category.onlineSubCategoriesCount > 0 &&
        Array.isArray(category.categories) &&
        category.categories.length > 0
    );
}

function MobileMenuCategory({
    category: rawCategory,
    expandedCategories,
    onToggle,
    onNavigate,
}: {
    category: ShopperProducts.schemas['Category'];
    expandedCategories: Set<string>;
    onToggle: (categoryId: string) => void;
    onNavigate: () => void;
}): ReactElement {
    const { t } = useTranslation('header');
    const enrichedCategory = useSubCategory(rawCategory.id);
    const category = enrichedCategory ?? rawCategory;
    const hasChildren = hasSubcategories(category);
    const isExpanded = expandedCategories.has(category.id);

    const renderSubcategoryLinks = (
        subcategories: ShopperProducts.schemas['Category'][] | undefined,
        level = 1
    ): ReactElement[] =>
        subcategories?.map((subcategory) => (
            <li key={subcategory.id}>
                <NavLink
                    to={routeHref(routes.category, { categoryId: subcategory.id })}
                    onClick={onNavigate}
                    className={cn(
                        'block py-2 text-sm font-medium hover:opacity-70 transition-opacity',
                        level > 1 && 'text-header-foreground/80'
                    )}>
                    {subcategory.name}
                </NavLink>
                {subcategory.categories?.length ? (
                    <ul className="pl-4 space-y-1">{renderSubcategoryLinks(subcategory.categories, level + 1)}</ul>
                ) : null}
            </li>
        )) ?? [];

    return (
        <li>
            <div className="flex items-center justify-between">
                <NavLink
                    to={routeHref(routes.category, { categoryId: category.id })}
                    onClick={onNavigate}
                    className="flex-1 py-3 text-base font-medium hover:opacity-70 transition-opacity">
                    {category.name}
                </NavLink>

                {hasChildren && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onToggle(category.id)}
                        className="ml-2 p-2 h-auto shrink-0 hover:bg-transparent hover:opacity-50 transition-opacity"
                        aria-label={
                            isExpanded
                                ? t('collapseCategory', {
                                      category: category.name,
                                      defaultValue: `Collapse ${category.name}`,
                                  })
                                : t('expandCategory', {
                                      category: category.name,
                                      defaultValue: `Expand ${category.name}`,
                                  })
                        }
                        aria-expanded={isExpanded}>
                        <ChevronDown
                            className={cn('size-5 transition-transform duration-200', {
                                'rotate-180': isExpanded,
                            })}
                        />
                    </Button>
                )}
            </div>

            {hasChildren && isExpanded && (
                <ul className="pl-4 pb-2 space-y-1 border-l border-header-foreground/10">
                    {renderSubcategoryLinks(category.categories)}
                </ul>
            )}
        </li>
    );
}

/**
 * MobileMenuDropdown - Renders the mobile menu dropdown content with expandable subcategories
 * Uses absolute positioning (relative to header) to automatically appear below the header
 * regardless of header height changes. No hardcoded values needed.
 */
export function MobileMenuDropdown(): ReactElement | null {
    const context = useMobileMenu();
    const { t } = useTranslation('header');
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

    if (!context) return null;

    const toggleCategory = (categoryId: string) => {
        setExpandedCategories((prev) => {
            const next = new Set(prev);
            if (next.has(categoryId)) {
                next.delete(categoryId);
            } else {
                next.add(categoryId);
            }
            return next;
        });
    };

    return (
        <div
            className={cn(
                'lg:hidden absolute left-0 right-0 top-full bg-header-background text-header-foreground shadow-lg z-40 max-h-[70vh] overflow-y-auto',
                { hidden: !context.isOpen }
            )}
            aria-hidden={!context.isOpen}>
            <nav className="px-4 py-4" aria-label={t('mobileNavigation', 'Mobile navigation menu')}>
                <a
                    href={DONATE_HREF}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={context.close}
                    data-testid="mobile-nav-donate-cta"
                    className="mb-4 flex items-center justify-center px-5 py-3 rounded-full text-sm font-semibold bg-header-accent-background text-header-accent-foreground transition-all btn-cta-donate">
                    Donate Now
                </a>

                <ul className="space-y-1">
                    {context.categories.map((category) => (
                        <MobileMenuCategory
                            key={category.id}
                            category={category}
                            expandedCategories={expandedCategories}
                            onToggle={toggleCategory}
                            onNavigate={context.close}
                        />
                    ))}
                </ul>
            </nav>
        </div>
    );
}

/**
 * ResponsiveNavigationMenu - A unified responsive navigation component
 *
 * This component uses CSS and Tailwind to adapt the same navigation structure
 * for both mobile and desktop:
 * - On mobile (< 1024px): Hamburger button with expandable vertical menu
 * - On desktop (>= 1024px): Horizontal mega menu with dropdown navigation
 *
 * The component renders a single navigation structure with responsive classes
 * controlling layout, visibility, and interaction patterns. This minimizes DOM bloat
 * while maintaining SSR compatibility.
 *
 * @param props - Component props
 * @param props.resolve - Promise resolving to root categories and first-level subcategories
 * @param props.defer - Promise resolving to deeper subcategory data for prefetch
 * @returns A responsive navigation component with CSS-controlled responsive behavior
 */
export default function ResponsiveNavigationMenu({
    resolve,
    defer,
}: ComponentPropsWithoutRef<typeof WithCategoryNavigationMenu>): ReactElement {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const { t } = useTranslation('header');
    const navigate = useNavigate();

    // Stable store instance for the mega-menu's Page Designer content slots (`menu-{categoryId}` /
    // `menu-{categoryId}-ribbon`), keyed by top-level category id. Filled on demand when a dropdown opens
    // (see useNavContentSlotLoader) rather than for every category on every document request, so critical
    // nav-link rendering is never gated on this non-critical promotional content. Subscribing here (rather than
    // in a descendant) makes this component re-render — and therefore recompute propsContent/renderSlotListAfter
    // below — once a category's slot data arrives.
    const navSlotStoreRef = useRef<ReturnType<typeof createNavContentSlotStore> | null>(null);
    if (!navSlotStoreRef.current) {
        navSlotStoreRef.current = createNavContentSlotStore();
    }
    useSyncExternalStore(
        navSlotStoreRef.current.subscribe,
        navSlotStoreRef.current.getSnapshot,
        navSlotStoreRef.current.getSnapshot
    );
    const loadNavContentSlot = useNavContentSlotLoader(navSlotStoreRef.current);

    const defaultListStyle = {
        width: '100%',
        maxWidth: '100%',
    };

    // Handler for top-level category clicks
    const handleTopLevelClick = useCallback(
        (categoryId: string) => {
            void navigate(routeHref(routes.category, { categoryId }));
        },
        [navigate]
    );

    // Element props generator
    const getElementProps = useCallback(
        ({
            level,
            category,
            isLeaf,
        }: {
            level: number;
            category: ShopperProducts.schemas['Category'];
            isLeaf?: boolean;
        }) => {
            const isSubcategory = level >= 1;
            const isClickableParent = level === 0 && !isLeaf && category.id;

            return {
                className: cn(
                    'text-sm !font-normal leading-5',
                    // Font color shifts to the mockup's red (#e50914 — distinct from the
                    // existing --header-accent-background token used elsewhere in the
                    // header, per explicit confirmation to match the mockup exactly) on
                    // hover/focus. Default subtle background tint from NavigationMenuLink
                    // is left as-is, not overridden.
                    isSubcategory && 'hover:text-[#e50914] focus:text-[#e50914] transition-colors'
                ),
                ...(isClickableParent && {
                    // Use onPointerDown instead of onClick for mouse-only navigation.
                    // This preserves keyboard accessibility: Enter/Space on the trigger
                    // expands the dropdown (Radix behavior), while mouse clicks navigate
                    // to the category page. Without this guard, keyboard users would be
                    // forced to navigate without being able to explore subcategories.
                    onPointerDown: (e: React.PointerEvent) => {
                        if (e.pointerType === 'mouse') {
                            handleTopLevelClick(category.id);
                        }
                    },
                }),
            };
        },
        [handleTopLevelClick]
    );

    // Which content (if any) a top-level category's column 2 should show: the legacy
    // `c_headerMenuBanner` aside always wins when configured (zero regression for
    // categories already using it); otherwise the new Page Designer content slot, if any.
    const getColumn2Kind = (category: ShopperProducts.schemas['Category']): 'banner' | 'content' | null => {
        const categoryId = category.id;
        if (hasBanner(category)) {
            return 'banner';
        }
        const slot = navSlotStoreRef.current?.getSnapshot().get(categoryId);
        return hasRegionContent(slot?.content, 'content') ? 'content' : null;
    };

    return (
        <WithCategoryNavigationMenu resolve={resolve} defer={defer}>
            {({ categories }) => {
                const mobileMenuContext: MobileMenuContextType = {
                    isOpen: mobileMenuOpen,
                    toggle: () => setMobileMenuOpen(!mobileMenuOpen),
                    close: () => setMobileMenuOpen(false),
                    categories,
                };

                return (
                    <MobileMenuContext.Provider value={mobileMenuContext}>
                        {/* Mobile: Hamburger button + "Menu" label */}
                        <div className="flex flex-col items-center lg:hidden">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={mobileMenuContext.toggle}
                                className="hover:bg-transparent hover:opacity-50 transition-opacity"
                                aria-label={mobileMenuOpen ? t('closeMenu', 'Close menu') : t('openMenu', 'Open menu')}
                                aria-expanded={mobileMenuOpen}>
                                {mobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
                            </Button>
                            <span className="text-xs font-medium -mt-1" aria-hidden="true">
                                {t('menuLabel', 'Menu')}
                            </span>
                        </div>

                        {/* Desktop: Mega menu (always rendered, hidden on mobile with CSS) */}
                        <div className="hidden lg:flex items-center h-full">
                            <CategoryNavigationMenu
                                categories={categories}
                                // ~200ms debounce before opening, so a cursor passing over a trigger on its
                                // way to the flyout doesn't cause spurious opens/closes.
                                delayDuration={200}
                                // True per-trigger floating popover: disables the shared/group-centered
                                // Viewport so each NavigationMenuContent positions itself relative to its
                                // own NavigationMenuItem (which is `position: relative`) instead of a single
                                // panel shared across all top-level items.
                                viewport={false}
                                propsContentContainer={() => ({
                                    className: cn(
                                        // Fixed 780px popover, centered directly under this item's own
                                        // trigger — matches the reviewed mockup exactly (not responsive).
                                        // NavigationMenuItem only spans the trigger's own width, so this can
                                        // overflow the viewport for a trigger near the left/right edge —
                                        // verify in-browser for edge categories.
                                        'left-1/2 z-[1000] mt-3 w-[780px] -translate-x-1/2',
                                        // Popover-card treatment matching the mockup exactly: 4px corners,
                                        // #dcdcdc border, and its precise elevation shadow. Base component
                                        // sets overflow-hidden scoped to group-data-[viewport=false] — an
                                        // unscoped `overflow-visible` here wouldn't reliably win (tailwind-
                                        // merge only dedupes same-modifier utilities, so both would ship and
                                        // the browser's cascade order would decide, non-deterministically).
                                        // Matching the exact modifier guarantees this always wins, so the
                                        // caret is never clipped whenever the popover is shown.
                                        'group-data-[viewport=false]/navigation-menu:overflow-visible rounded border border-[#dcdcdc] bg-header-menu-background text-header-menu-foreground shadow-[0_12px_32px_rgba(0,0,0,0.15)]',
                                        // Top-pointing caret: the classic bordered-tooltip-arrow recipe — a
                                        // rotated square whose only *colored* edges are border-top + border-
                                        // left, rather than a full outline. After a 45deg rotation, that
                                        // top-left corner (where the two borders meet) becomes the caret's
                                        // topmost point, and the two 1px strokes fan out from it as a "^"
                                        // outline — border-right/border-bottom stay at the preflight-default
                                        // 0 width, since post-rotation those edges face down/inward where no
                                        // outline is wanted. ::before draws that outline (bigger, so its tip
                                        // still pokes out past ::after below); ::after is a smaller, solid-
                                        // fill (no border) square, same center, that masks the panel's own
                                        // 1px top border directly underneath — both squares are centered
                                        // exactly ON that border line (top-0 -translate-y-1/2), so ::after's
                                        // fill fully paints over that segment instead of sitting flush above
                                        // it, and the border-top/left strokes it doesn't cover read as
                                        // continuing straight out of the panel's own border rather than
                                        // floating over it. Already centered under this item's own trigger,
                                        // so — unlike a shared-viewport approach — the caret doesn't need to
                                        // track anything.
                                        "before:content-[''] before:absolute before:top-0 before:left-1/2 before:z-10 before:h-4 before:w-4 before:-translate-x-1/2 before:-translate-y-1/2 before:rotate-45 before:border-t before:border-l before:border-[#dcdcdc]",
                                        "after:content-[''] after:absolute after:top-0 after:left-1/2 after:z-10 after:h-3.5 after:w-3.5 after:-translate-x-1/2 after:-translate-y-1/2 after:rotate-45 after:bg-header-menu-background",
                                        '!p-0'
                                    ),
                                })}
                                propsContent={({ category }) => {
                                    // Grid, not flex: the title ("Shop All X") and link list both live in
                                    // column 1 stacked across two implicit rows (see renderSlotListBefore /
                                    // propsList below), while column 2 spans both of those rows via
                                    // row-span-2 so it visually aligns with the title+list's combined
                                    // height. The ribbon (a separate sibling, not part of this grid's first
                                    // two rows) auto-places into its own row via col-span-2, since rows 1-2
                                    // are already fully occupied. When column 2 has nothing to show, the
                                    // 2-column template itself is omitted — a plain 1-column grid still
                                    // stacks the title above the list, but reserves no empty second column,
                                    // matching today's link-list-only layout exactly.
                                    const hasColumn2 = Boolean(getColumn2Kind(category));
                                    return {
                                        className: cn(
                                            'grid grid-flow-dense',
                                            hasColumn2 && 'min-h-[280px] md:grid-cols-[minmax(210px,auto)_1fr]'
                                        ),
                                    };
                                }}
                                renderSlotListBefore={({ parent, level }) => {
                                    if (level !== 1 || !parent) {
                                        return;
                                    }
                                    // Divider (see propsList below) needs to span the title's height too, not
                                    // just the list's — applying the same border-r to both, with padding
                                    // (not margin) providing the gap between them, keeps the line continuous.
                                    const hasColumn2 = Boolean(getColumn2Kind(parent));
                                    return (
                                        <h3
                                            className={cn(
                                                'text-header-foreground md:col-start-1 md:px-6 md:pt-6 md:pb-4 text-[0.95rem] font-bold',
                                                hasColumn2 && 'md:border-r md:border-[#e2e8f0]'
                                            )}>
                                            {t('shopAllCategory', {
                                                category: parent.name,
                                                defaultValue: `Shop All ${parent.name}`,
                                            })}
                                        </h3>
                                    );
                                }}
                                propsList={({ parent, categories: subCategories, level }) => {
                                    if (level === 1) {
                                        // Thin (1px) light-gray divider separating the link list from column
                                        // 2, only when column 2 actually has something to show.
                                        const hasColumn2 = Boolean(parent && getColumn2Kind(parent));
                                        const columnLinksClassName = cn(
                                            'md:col-start-1 md:px-6 md:pb-6',
                                            hasColumn2 && 'md:border-r md:border-[#e2e8f0]'
                                        );
                                        if (isVertical(parent)) {
                                            return {
                                                style: defaultListStyle,
                                                className: cn('flex flex-col gap-3 p-0', columnLinksClassName),
                                            };
                                        }
                                        return {
                                            style: {
                                                ...defaultListStyle,
                                                gridTemplateColumns: `repeat(${subCategories.length}, minmax(0, 1fr))`,
                                            },
                                            className: cn('grid p-0', columnLinksClassName),
                                        };
                                    }
                                }}
                                propsElement={getElementProps}
                                // Item value = category id, so onValueChange tells us which dropdown opened
                                propsListItem={({ category }) => ({ value: category.id })}
                                onValueChange={loadNavContentSlot}
                                renderSlotListAfter={({ level, parent }) => {
                                    if (level !== 1 || !parent) {
                                        return;
                                    }
                                    const slot = navSlotStoreRef.current?.getSnapshot().get(parent.id);
                                    const contentPage = slot?.content;
                                    const ribbonPage = slot?.ribbon;
                                    const showBanner = hasBanner(parent);

                                    if (
                                        !showBanner &&
                                        !hasRegionContent(contentPage, 'content') &&
                                        !hasRegionContent(ribbonPage, 'content')
                                    ) {
                                        return;
                                    }

                                    return (
                                        <>
                                            {showBanner ? (
                                                <aside className="self-stretch md:col-start-2 md:row-span-2 md:pl-6">
                                                    <CategoryBanner category={parent} />
                                                </aside>
                                            ) : (
                                                hasRegionContent(contentPage, 'content') && (
                                                    <aside className="self-stretch md:col-start-2 md:row-span-2">
                                                        <CategoryDropdownContentSlot page={contentPage} />
                                                    </aside>
                                                )
                                            )}
                                            {hasRegionContent(ribbonPage, 'content') && (
                                                <CategoryDropdownRibbon page={ribbonPage} className="md:col-span-2" />
                                            )}
                                        </>
                                    );
                                }}
                            />
                        </div>

                        {/* Mobile: Menu dropdown (rendered here to be inside provider) */}
                        <MobileMenuDropdown />
                    </MobileMenuContext.Provider>
                );
            }}
        </WithCategoryNavigationMenu>
    );
}
